<template>
	<div class="container full-height">
		<Breadcrumbs v-if="!isSmallDevice" :items="breadcrumb" />
		<NotFoundContent v-if="isError" />
		<LoadingSpinner v-else-if="isLoading" />
		<div v-else class="content">
			<div v-if="dailyPuzzleInfo" class="info">
				<div class="full-height">
					<div class="top-container">
						<div class="nav">
							<router-link
								:aria-label="
									$t(
										'WordscapesDailyIndexPageSeeMoreAriaLabel'
									)
								"
								:to="
									$t(
										'WordscapesDailyDetailInternalLinksTableHeadingUrl'
									)
								"
								class="btn"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									height="24"
									viewBox="0 0 24 24"
									width="24"
								>
									<path d="M0 0h24v24H0z" fill="none" />
									<path
										d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"
									/>
								</svg>
								{{ btnText }}
							</router-link>
							<h1 v-html="title" />
						</div>
						<Board :board="dailyPuzzleInfo.board" :color="color" />
						<p
							v-if="isExtraLargeDevice"
							class="text"
							v-html="templateText"
						/>
						<PuzzleList
							v-if="isExtraLargeDevice && dateList.length"
							:title="
								$t(
									'WordscapesDailyDetailInternalLinksTableHeading'
								)
							"
							:date="date"
							:date-list="dateList"
						/>
					</div>
					<div v-if="isSmallDevice" class="advertising-container">
						<Advertising :stub="AD_LAYOUT_STUBS.MOB_IN_CONTENT" />
					</div>
				</div>
				<div>
					<BoardCard
						v-if="dailyPuzzleInfo.bonus_words.length"
						:title="bonusWordsTitle"
						:color="color"
						:words="dailyPuzzleInfo.bonus_words"
						:is-bonus-words="true"
					/>

					<BoardCard
						:title="wordsTitle"
						:color="color"
						:words="dailyPuzzleInfo.words"
					/>
				</div>
				<p
					v-if="!isExtraLargeDevice"
					class="text"
					v-html="templateText"
				/>
				<PuzzleList
					v-if="!isExtraLargeDevice && dateList.length"
					:title="
						$t('WordscapesDailyDetailInternalLinksTableHeading')
					"
					:date="date"
					:date-list="dateList"
				/>
			</div>
			<aside v-if="!isSmallDevice" class="sidebar">
				<Advertising
					:stub="AD_LAYOUT_STUBS.RIGHT_RAIL"
					:style="{ top: `${topElementsHeight + 25}px` }"
					class="advertising--sticky"
				/>
			</aside>
		</div>
	</div>
</template>

<script>
const Advertising = () =>
	import(
		/* webpackChunkName: "component-advertising" */ "@/components/advertising"
	);
const Breadcrumbs = () =>
	import(
		/* webpackChunkName: "component-breadcrumbs" */ "@/components/breadcrumbs"
	);
const Board = () =>
	import(/* webpackChunkName: "component-board" */ "@/components/board");
const BoardCard = () =>
	import(
		/* webpackChunkName: "component-board-card" */ "@/components/board-card"
	);
const PuzzleList = () =>
	import(
		/* webpackChunkName: "component-puzzle-list" */ "@/components/puzzle-list"
	);
const LoadingSpinner = () =>
	import(
		/* webpackChunkName: "component-loading-spinner" */ "@/components/loading-spinner"
	);
import NotFoundContent from "@/pages/not-found";
import { DAILY_PUZZLE_MIN_DATE, AD_LAYOUT_STUBS } from "@/common/consts";
import { getDailyPuzzleInfo } from "@/common/services/search";
import { capitalizeFirstLetter, dateAsObject } from "@/common/functions";
import { DAILY_COLORS } from "@/common/daily_colors";

import metaInfo from "./meta";
import { mapGetters } from "vuex";

export default {
	name: "DailyPuzzleAnswers",
	components: {
		Advertising,
		Breadcrumbs,
		Board,
		BoardCard,
		PuzzleList,
		LoadingSpinner,
		NotFoundContent,
	},
	data: function() {
		return {
			AD_LAYOUT_STUBS,
			dailyPuzzleInfo: null,
			date: {
				day: null,
				month: null,
				year: null,
			},
			isLoading: false,
			isError: false,
			dateList: [],
		};
	},
	computed: {
		...mapGetters(["topElementsHeight"]),
		title() {
			return this.$t("WordscapesDailyDetailPageH1", {
				Month: this.date.month,
				DayOrdinalFormat: this.date.day,
				Year: this.date.year,
			});
		},
		bonusWordsTitle() {
			return this.$t("WordscapesDailyDetailPageBonusTableHeading", {
				Month: this.date.month,
				DayOrdinalFormat: this.date.day,
				Year: this.date.year,
			});
		},
		wordsTitle() {
			return this.$t("WordscapesDailyDetailResultsTableHeading", {
				NumberOfWords: this.dailyPuzzleInfo.words.length,
				Month: this.date.month,
				DayOrdinalFormat: this.date.day,
				Year: this.date.year,
			});
		},
		btnText() {
			return this.isSmallOrMediumDevice
				? this.$t("WordscapesDailyIndexPageSeeMoreText").substring(4)
				: this.$t("WordscapesDailyIndexPageSeeMoreText");
		},
		color() {
			const month = new Date(Object.values(this.date)).getMonth();
			return DAILY_COLORS[month][this.date.day - 1];
		},
		templateText() {
			return this.$t("WordscapesDailyDetailPageText", {
				Month: this.date.month,
				DayOrdinalFormat: this.date.day,
				Year: this.date.year,
			});
		},
		breadcrumb() {
			return [
				{
					label: this.$t("BreadcrumbWordscapesDailyIndexPageText"),
					url: this.$t("BreadcrumbWordscapesDailyIndexPageUrl"),
				},
				{
					label: this.$t("BreadcrumbWordscapesDailyDetailPageText", {
						Month: this.date.month,
						DayOrdinalFormat: this.date.day,
						Year: this.date.year,
					}),
				},
			];
		},
	},
	mounted() {
		this.getPageInfo();
	},
	beforeRouteUpdate(to, from, next) {
		next();
		this.dateList = [];
		this.getPageInfo();
	},
	methods: {
		parseUrlParams() {
			// RegEx check
			const dateArray = this.$route.params.date.split("-") || [];
			const MONTHS = [
				"january",
				"february",
				"march",
				"april",
				"may",
				"june",
				"july",
				"august",
				"september",
				"october",
				"november",
				"december",
			];
			if (dateArray.length !== 4) {
				this.isError = true;
				window.document.dispatchEvent(
					new CustomEvent("spa-render-404")
				);
			} else if (
				MONTHS.indexOf(dateArray[0]) === -1 ||
				parseInt(dateArray[1]) < 1 ||
				parseInt(dateArray[1]) > 31 ||
				!parseInt(dateArray[2]) ||
				parseInt(dateArray[2]) < 2019
			) {
				this.isError = true;
				window.document.dispatchEvent(
					new CustomEvent("spa-render-404")
				);
			} else {
				if (dateArray.length === 4 && dateArray[3] === "answers") {
					this.date.month = capitalizeFirstLetter(dateArray[0]);
					this.date.day = dateArray[1];
					this.date.year = dateArray[2];
				}
			}
		},
		async getDailyPuzzleAnswers() {
			this.isLoading = true;
			this.isError = false;
			const dailyPuzzleInfo = this.date
				? await getDailyPuzzleInfo(
						this.date.year,
						new Date(Object.values(this.date)).getMonth() + 1,
						this.date.day
				  )
				: null;
			if (dailyPuzzleInfo === null) {
				this.isError = true;
			}
			this.isLoading = false;
			this.dailyPuzzleInfo = dailyPuzzleInfo;
			// 'stage' is the day, 'world' is the month
			if (this.date.day !== dailyPuzzleInfo.stage) {
				this.date.day = dailyPuzzleInfo.stage;
				this.date.month = new Date(
					dailyPuzzleInfo.world
				).toLocaleString("en-US", {
					month: "long",
				});
				this.updateRoute();
			}
		},
		updateRoute() {
			this.$router.replace({
				params: {
					date: `${this.date.month.toLowerCase()}-${this.date.day}-${
						this.date.year
					}-answers`,
				},
			});
		},
		getLastDays() {
			for (let i = 1; i < 4; i++) {
				const date = new Date(
					`${this.date.month} ${this.date.day}, ${this.date.year}`
				);
				date.setDate(date.getDate() - i);
				if (new Date(date) < DAILY_PUZZLE_MIN_DATE) {
					return;
				}
				this.dateList.push(dateAsObject(date));
			}
		},
		getPageInfo() {
			this.parseUrlParams();
			if (!this.isError) {
				this.getDailyPuzzleAnswers();
				this.getLastDays();
			}
		},
	},
	metaInfo,
};
</script>

<style lang="scss" src="./styles.scss" scoped></style>
